/**
 * Building blocks shared by the detail pages (document, email): the side-panel
 * card and its label/value rows, and the copy-to-clipboard button. The
 * request-status vocabulary lives beside it in requestStatus.js.
 */
import { useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'

export function Dot() {
  return <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">·</span>
}

// Copies `value` (a string, or a function returning one) and swaps its icon
// for a check that pops in, with the label announced to screen readers.
export function CopyButton({ value, label, copiedLabel = 'Copied', icon: Icon = Copy, className = 'h-7 w-7', iconClassName = 'h-4 w-4' }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])

  const copy = async () => {
    const text = typeof value === 'function' ? value() : value
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // Clipboard API blocked (insecure context): fall back to a hidden field.
      const field = window.document.createElement('textarea')
      field.value = text
      field.setAttribute('readonly', '')
      field.style.position = 'fixed'
      field.style.opacity = '0'
      window.document.body.appendChild(field)
      field.select()
      window.document.execCommand('copy')
      field.remove()
    }
    setCopied(true)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied(false), 1800)
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? copiedLabel : label}
      aria-label={copied ? copiedLabel : label}
      className={`relative inline-flex flex-shrink-0 items-center justify-center rounded-lg border transition-all duration-200 active:scale-95 ${
        copied
          ? 'border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-300'
          : 'border-gray-200 bg-white/80 text-gray-500 hover:border-gray-300 hover:bg-white hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
      } ${className}`}
    >
      {copied
        ? <Check key="done" className={`animate-copied ${iconClassName}`} />
        : <Icon key="idle" className={`animate-panel-in ${iconClassName}`} />}
      <span className="sr-only" aria-live="polite">{copied ? copiedLabel : ''}</span>
    </button>
  )
}

export function PanelCard({ title, action, delay, children }) {
  return (
    <section
      className="rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] animate-rise-in dark:border-gray-800 dark:bg-gray-900"
      style={{ '--rise-delay': delay }}
    >
      <div className="flex items-center justify-between gap-2 px-4 pb-1 pt-3.5">
        <h2 className="text-[13px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  )
}

export function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 px-4 py-2.5">
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
      <dt className="w-24 flex-shrink-0 text-sm text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  )
}
